import React, { useContext, useEffect, useRef, useState } from "react";
import { useTransition, animated } from "@react-spring/web";
import { useParams } from "react-router-dom";

import Actions from "src/components/actionbar";
import Reveal from "src/components/animation";
import Button from "src/components/button";
import Slider from "src/components/slider";
import { Input } from "src/components/input";

import { StoryboardItem } from "src/types/experiences/storyboards";
import { EditMode, StoryboardsContext } from "../../context";
import {
  useCloneExperiencesStoryboard,
  useDeleteExperiencesStoryboard,
} from "src/query/experiences/storyboards";

import styles from "./edit.module.scss";
import { StoryboardCard } from "../storyboardCard";
import {
  buildCleverstoryUrl,
  buildPageTargetPath,
} from "src/pages/experiences/utils/cleverstoryRedirect";
// eslint-disable-next-line no-undef
const imageCdn = process.env.REACT_APP_IMAGE_CDN

// ─── ExperiencesMenuBar ───────────────────────────────────────────────────────

interface ExperiencesMenuBarProps {
  show: boolean;
  query: string;
  onSearchChange: (q: string | null) => void;
  onEditModeChange: (mode: EditMode) => void;
}

export const ExperiencesMenuBar: React.FC<ExperiencesMenuBarProps> = ({
  show,
  query,
  onSearchChange,
  onEditModeChange,
}) => {
  const [transitions, api] = useTransition(show, () => ({
    initial: { opacity: 0, top: "0px" },
    from:    { opacity: 1, top: "-90px" },
    enter:   { opacity: 1, top: "0px"   },
    leave:   { opacity: 0, top: "-90px" },
  }));

  useEffect(() => {
    api.start();
  }, [show]);

  return transitions(
    (style, item) =>
      item && (
        <animated.div style={style} className={styles.experiencesMenuBar}>
          <Actions.Container>
            <Actions.Row>
              <Actions.Search query={query} onSearch={onSearchChange} />
              <Actions.IconButton
                icon="paperflite-copy"
                tooltip="Clone"
                onClick={() => onEditModeChange("clone")}
              />
              <Actions.IconButton
                icon="paperflite-rename"
                tooltip="Rename folder"
                onClick={() => onEditModeChange("rename")}
              />
              <Actions.IconButton
                icon="paperflite-delete"
                tooltip="Delete"
                onClick={() => onEditModeChange("delete")}
              />
            </Actions.Row>
          </Actions.Container>
        </animated.div>
      )
  );
};

// ─── CloneMenubar ─────────────────────────────────────────────────────────────

interface CloneMenubarProps {
  show: boolean;
  onCancel: () => void;
}

export const CloneMenubar: React.FC<CloneMenubarProps> = ({ show, onCancel }) => {
  const { selectedStoryboards } = useContext(StoryboardsContext);
  // Clone is single-select — the page keeps at most one entry in clone mode.
  const selectedStoryboard = selectedStoryboards[0];

  return (
    <>
      <Reveal show={show} className={styles.modeConfirmation}>
        <div className={styles.message}>
          Click on a storyboard to clone.
        </div>
        <Button variant="secondary" text="Cancel" onClick={onCancel} />
      </Reveal>

      {show && selectedStoryboard && (
        <CloneConfirmationDialog
          show={!!selectedStoryboard}
          storyboard={selectedStoryboard}
          onClose={onCancel}
        />
      )}
    </>
  );
};

// ─── RenameFolderMenubar ──────────────────────────────────────────────────────

interface RenameFolderMenubarProps {
  show: boolean;
  saving: boolean;
  onSave: () => void;
  onCancel: () => void;
}

export const RenameFolderMenubar: React.FC<RenameFolderMenubarProps> = ({
  show,
  saving,
  onSave,
  onCancel,
}) => (
  <Reveal show={show} className={styles.modeConfirmation}>
    <div className={styles.message}>
      Rename folders and storyboards, then save.
    </div>
    <Button
      variant="primary"
      icon="paperflite-tick"
      text={saving ? "Saving..." : "Save"}
      disabled={saving}
      onClick={onSave}
    />
    <Button
      variant="secondary"
      text="Cancel"
      disabled={saving}
      onClick={onCancel}
    />
  </Reveal>
);

// ─── DeleteMenubar ────────────────────────────────────────────────────────────

interface DeleteMenubarProps {
  show: boolean;
  onCancel: () => void;
}

export const DeleteMenubar: React.FC<DeleteMenubarProps> = ({ show, onCancel }) => {
  const { selectedStoryboards } = useContext(StoryboardsContext);
  const [confirm, setConfirm] = useState(false);

  return (
    <>
      <Reveal show={show} className={styles.modeConfirmation}>
        <div className={styles.message}>
          Click on one or more storyboards to delete.
        </div>
        <Button
          variant="primary"
          icon="paperflite-delete"
          text={
            selectedStoryboards.length > 0
              ? `Delete (${selectedStoryboards.length})`
              : "Delete"
          }
          disabled={selectedStoryboards.length === 0}
          onClick={() => setConfirm(true)}
        />
        <Button variant="secondary" text="Cancel" onClick={onCancel} />
      </Reveal>

      {confirm && selectedStoryboards.length > 0 && (
        <DeleteConfirmationDialog
          show={confirm}
          storyboards={selectedStoryboards}
          onClose={() => setConfirm(false)}
          onFinish={() => {
            setConfirm(false);
            onCancel();
          }}
        />
      )}
    </>
  );
};

// ─── CloneConfirmationDialog ──────────────────────────────────────────────────

interface CloneConfirmationDialogProps {
  show: boolean;
  storyboard: StoryboardItem;
  onClose: () => void;
}

const CloneConfirmationDialog: React.FC<CloneConfirmationDialogProps> = ({
  show,
  storyboard,
  onClose,
}) => {
  const { onEditModeChange, onStoryboardSelect } = useContext(StoryboardsContext);
  const workspaceId = useWorkspaceId();

  const { mutate: clone, isPending } =
    useCloneExperiencesStoryboard(workspaceId);

  const [name, setName] = useState("");
  const [error, setError] = useState<string | null>(null);
  // Tab must be opened synchronously on the click (transient user activation),
  // otherwise the popup blocker blocks it once the async clone resolves. We then
  // just point this already-open tab at the editor when cloning succeeds.
  const editorWindowRef = useRef<Window | null>(null);

  const handleClose = () => {
    setName("");
    setError(null);
    onStoryboardSelect(null);
    onEditModeChange(null);
    onClose();
  };

  const validate = () => {
    if (!name.trim()) return "Please provide a valid name";
    if (name.trim().length > 200) return "Name cannot exceed 200 characters";
    return null;
  };

  const handleConfirm = () => {
    const err = validate();
    if (err) { setError(err); return; }

    // Open the tab now, inside the click gesture, so the popup blocker allows it.
    editorWindowRef.current = window.open("", "_blank");

    clone(
      { id: storyboard.id!, name: name.trim() },
      {
        onSuccess: (cloned) => {
          // Point the already-open tab at the cloned storyboard's editor — same
          // target as the card edit icon (/app/:workspace/pages/:pageId).
          const clonedPageId = cloned?.page?.id;
          if (clonedPageId && editorWindowRef.current) {
            editorWindowRef.current.location.href = buildCleverstoryUrl(
              buildPageTargetPath(workspaceId, clonedPageId),
            );
          } else {
            editorWindowRef.current?.close();
          }
          handleClose();
        },
        onError: () => {
          editorWindowRef.current?.close();
        },
      },
    );
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") handleConfirm();
    if (e.key === "Escape") handleClose();
  };

  return (
    <Slider.Dialog
      className={styles.cloneConfirmDialog}
      width="300"
      show={show}
      onClose={handleClose}
    >
      <Slider.Header title="cloning..." message="1 storyboard" />
      <Slider.Body>
        <div className={styles.preview}>
          <StoryboardCard storyboard={storyboard} workspaceId={workspaceId} readOnly={true} />
        </div>
        <Input
          autoFocus
          placeholder="Add a storyboard name..."
          value={name}
          onChange={(e) => { setName(e.target.value); setError(null); }}
          onKeyDown={handleKeyDown}
          disabled={isPending}
          maxLength={200}
        />
        <Reveal show={!!error}>
          <div className={styles.error}>
            <i className="fa-solid fa-triangle-exclamation" />
            {error}
          </div>
        </Reveal>
        <p className={styles.description}>
          Clone away! Create an exact copy of this storyboard to tweak and share.
        </p>
      </Slider.Body>
      <Slider.Actions>
        <Button
          variant="primary"
          icon="paperflite-tick"
          text={isPending ? "Cloning..." : "Confirm"}
          disabled={isPending}
          onClick={handleConfirm}
        />
        <Button
          text="Cancel"
          variant="secondary"
          disabled={isPending}
          onClick={handleClose}
        />
      </Slider.Actions>
    </Slider.Dialog>
  );
};

// ─── DeleteConfirmationDialog ─────────────────────────────────────────────────

interface DeleteConfirmationDialogProps {
  show: boolean;
  storyboards: StoryboardItem[];
  onClose: () => void;
  onFinish: () => void;
}

const DeleteConfirmationDialog: React.FC<DeleteConfirmationDialogProps> = ({
  show,
  storyboards,
  onClose,
  onFinish,
}) => {
  const workspaceId = useWorkspaceId();

  const { mutateAsync: deleteStoryboard } =
    useDeleteExperiencesStoryboard(workspaceId);

  const [deleting, setDeleting] = useState(false);

  const count = storyboards.length;

  const handleConfirm = async () => {
    setDeleting(true);
    try {
      // Fire all deletes; each one optimistically drops its card from the list
      // cache (and invalidates) on success, so the grid clears as they resolve.
      await Promise.all(
        storyboards.map((sb) =>
          sb.id ? deleteStoryboard(sb.id) : Promise.resolve(),
        ),
      );
    } catch {
      // A failed delete still leaves the rest applied; the list invalidation in
      // the mutation reconciles what actually got removed.
    } finally {
      setDeleting(false);
      onFinish();
    }
  };

  return (
    <Slider.Dialog
      className={styles.deleteConfirmDialog}
      width="300"
      show={show}
      onClose={onClose}
    >
      <Slider.Header
        title={`Delete ${count > 1 ? "these storyboards" : "this storyboard"}?`}
        message={`${count} storyboard${count > 1 ? "s" : ""}`}
      />
      <Slider.Body>
        <p className={styles.description}>
          Are you sure you want to delete{" "}
          {count > 1 ? "these storyboards" : "this storyboard"}? This action
          cannot be undone.
          <span className={styles.highlight}>
            {count > 1 ? "They" : "The storyboard"} will be permanently removed.
          </span>
        </p>
        <div className={styles.deleteGrid}>
          {storyboards.map((sb) => (
            <StoryboardCard
              key={sb.id}
              storyboard={sb}
              workspaceId={workspaceId}
              readOnly={true}
            />
          ))}
        </div>
      </Slider.Body>
      <Slider.Actions>
        <Button
          variant="primary"
          icon="paperflite-tick"
          text={deleting ? "Deleting..." : "Confirm"}
          disabled={deleting}
          onClick={handleConfirm}
        />
        <Button
          text="Cancel"
          variant="secondary"
          disabled={deleting}
          onClick={onClose}
        />
      </Slider.Actions>
    </Slider.Dialog>
  );
};

// ─── Internal helper ──────────────────────────────────────────────────────────
const useWorkspaceId = (): string => {
  const { workspaceId } = useParams<{ workspaceId: string }>();
  return workspaceId ?? "";
};
